import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { Box3, Sphere, Vector3, type Object3D } from 'three'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { NOME_PRANCHA_ATIVA } from '../extracao/Prancha'
import type { EnquadramentoInterior } from '../extracao/enquadramentoCamera'
import {
  DISTANCIA_MAXIMA,
  DISTANCIA_MINIMA,
  PREFIXO_SELECIONAVEL,
  SEM_ROLAGEM,
  alvoCamera,
  posicaoDaVista,
  type ComandoCamera,
  type PostoCamera,
} from './comandosCamera'

/** Nome do grupo que a cena de estabilização dá ao apoio selecionado. */
const NOME_APOIO_SELECIONADO = 'selecao-equipamento'

/** Duração da transição suave entre enquadramentos, em milissegundos. */
const DURACAO_TRANSICAO_MS = 450

interface ControlesOrbita {
  target?: Vector3
  update?: () => void
  addEventListener?: (tipo: string, ouvinte: () => void) => void
  removeEventListener?: (tipo: string, ouvinte: () => void) => void
}

interface Pose {
  posicao: Vector3
  alvo: Vector3
}

interface Transicao {
  origem: Pose
  destino: Pose
  inicioMs: number
}

const prefereMovimentoReduzido = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Suavização ease-out: rápido no início, assenta no fim — sem ultrapassar o alvo. */
const suavizar = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * Aplica os comandos de câmera dentro do Canvas.
 *
 * Vive aqui porque precisa de `useThree`/`useFrame`, mas não decide conteúdo
 * didático: quem aciona é a trilha de ferramentas ou um "Enquadrar esta ação",
 * e o que cada comando significa está em `comandosCamera.ts` (poses genéricas,
 * testáveis sem WebGL) e em `extracao/enquadramentoCamera.ts` (poses
 * específicas da Extração). Aqui só resolve o alvo em coordenadas de mundo e
 * anima a transição.
 *
 * A transição é cancelável: começar a orbitar manualmente (evento `start` dos
 * controles) interrompe imediatamente, para o professor nunca "brigar" com uma
 * câmera em movimento. Com preferência por movimento reduzido, o enquadramento
 * troca na hora, sem animação.
 */
export function ControleCamera3D({
  comando,
  perfil,
  posto = SEM_ROLAGEM,
  enquadramentoInterior = null,
  enquadramentoOficina = null,
  aoInteragirManual = undefined,
}: {
  comando: ComandoCamera
  perfil: PerfilVeiculo
  /** Como o veículo está apoiado (rolagem/altura) — só a Extração gira o veículo. */
  posto?: PostoCamera
  /** Pose do comando "Interior", calculada por quem conhece a configuração do veículo. */
  enquadramentoInterior?: EnquadramentoInterior | null
  /** Pose de três quartos da peça em trabalho na oficina de corte. */
  enquadramentoOficina?: EnquadramentoInterior | null
  /** Notifica o modo que iniciou o foco automático quando a pessoa assume a órbita. */
  aoInteragirManual?: () => void
}) {
  const camera = useThree((estado) => estado.camera)
  const cena = useThree((estado) => estado.scene)
  const controles = useThree((estado) => estado.controls) as ControlesOrbita | null

  const transicaoRef = useRef<Transicao | null>(null)
  /** Enquadramento anterior ao último comando de vista/enquadramento, para "Voltar". */
  const anteriorRef = useRef<Pose | null>(null)

  // cancela a transição em andamento assim que o aluno/professor pega a órbita
  useEffect(() => {
    if (!controles?.addEventListener) return
    const cancelar = () => {
      transicaoRef.current = null
      aoInteragirManual?.()
    }
    controles.addEventListener('start', cancelar)
    return () => controles.removeEventListener?.('start', cancelar)
  }, [controles, aoInteragirManual])

  useEffect(() => {
    const alvoAtual = controles?.target
    if (!alvoAtual || comando.id === 0) return

    const destino =
      comando.tipo === 'voltarEnquadramento'
        ? anteriorRef.current
        : resolverDestino({
          comando,
          cena,
          camera,
          alvoAtual,
          perfil,
          posto,
          enquadramentoInterior,
          enquadramentoOficina,
        })
    if (!destino) return

    const origem: Pose = { posicao: camera.position.clone(), alvo: alvoAtual.clone() }

    // guarda o enquadramento anterior para "Voltar" — exceto o próprio "Voltar",
    // que não deve substituir a referência por onde a câmera estava só de passagem
    if (comando.tipo !== 'voltarEnquadramento') {
      anteriorRef.current = origem
    }

    if (prefereMovimentoReduzido()) {
      aplicarPose(camera, controles, destino)
      transicaoRef.current = null
      return
    }

    transicaoRef.current = { origem, destino, inicioMs: performance.now() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comando])

  useFrame(() => {
    const transicao = transicaoRef.current
    if (!transicao || !controles?.target) return

    const decorrido = performance.now() - transicao.inicioMs
    const t = Math.min(1, decorrido / DURACAO_TRANSICAO_MS)
    const f = suavizar(t)

    aplicarPose(camera, controles, {
      posicao: transicao.origem.posicao.clone().lerp(transicao.destino.posicao, f),
      alvo: transicao.origem.alvo.clone().lerp(transicao.destino.alvo, f),
    })

    if (t >= 1) transicaoRef.current = null
  })

  return null
}

function aplicarPose(
  camera: { position: Vector3; lookAt: (x: number, y: number, z: number) => void },
  controles: ControlesOrbita,
  pose: Pose,
): void {
  controles.target?.copy(pose.alvo)
  camera.position.copy(pose.posicao)
  camera.lookAt(pose.alvo.x, pose.alvo.y, pose.alvo.z)
  controles.update?.()
}

/** Resolve o destino (posição de câmera + alvo) de cada tipo de comando, em coordenadas de mundo. */
function resolverDestino(args: {
  comando: ComandoCamera
  cena: Object3D
  camera: CameraEnquadravel
  alvoAtual: Vector3
  perfil: PerfilVeiculo
  posto: PostoCamera
  enquadramentoInterior: EnquadramentoInterior | null
  enquadramentoOficina: EnquadramentoInterior | null
}): Pose | null {
  const { comando, cena, camera, alvoAtual, perfil, posto, enquadramentoInterior, enquadramentoOficina } = args

  const vista = posicaoDaVista(comando.tipo, perfil, posto)
  if (vista) {
    return { posicao: new Vector3(...vista), alvo: new Vector3(...alvoCamera(perfil, posto)) }
  }

  // A oficina não reaproveita a direção atual da câmera: a pose é absoluta,
  // para trocar de peça sempre chegar ao mesmo três quartos reconhecível.
  if (comando.tipo === 'enquadrarOficina') {
    if (!enquadramentoOficina) return null
    return {
      posicao: new Vector3(...enquadramentoOficina.posicao),
      alvo: new Vector3(...enquadramentoOficina.alvo),
    }
  }

  if (comando.tipo === 'interiorExtracao') {
    if (!enquadramentoInterior) return null
    return {
      posicao: new Vector3(...enquadramentoInterior.posicao),
      alvo: new Vector3(...enquadramentoInterior.alvo),
    }
  }

  if (comando.tipo === 'aproximarSeLonge') {
    const objeto = acharSelecionado(cena, comando.idAlvo)
    if (!objeto) return null
    const caixa = new Box3().expandByObject(objeto)
    if (caixa.isEmpty()) return null
    // de perto já se vê: não mexe na câmera
    if (camera.position.distanceTo(caixa.getCenter(new Vector3())) <= DISTANCIA_PARA_APROXIMAR_M) return null
    return enquadrarCaixa(caixa, camera, alvoAtual)
  }

  if (comando.tipo === 'enquadrarSelecao' || comando.tipo === 'alinhamentoVitimaPrancha') {
    const caixa = new Box3()
    const objetoPrincipal = acharSelecionado(cena, comando.idAlvo)
    if (objetoPrincipal) caixa.expandByObject(objetoPrincipal)

    if (comando.tipo === 'alinhamentoVitimaPrancha') {
      const prancha = cena.getObjectByName(NOME_PRANCHA_ATIVA)
      if (prancha) caixa.expandByObject(prancha)
    }

    if (caixa.isEmpty()) return null
    return enquadrarCaixa(caixa, camera, alvoAtual)
  }

  if (comando.tipo === 'enquadrarCena') {
    const caixa = new Box3()
    cena.traverse((objeto) => {
      if (objeto.name.startsWith(PREFIXO_SELECIONAVEL)) caixa.expandByObject(objeto)
    })
    if (caixa.isEmpty()) return null
    return enquadrarCena(caixa, camera, alvoAtual)
  }

  // aproximar / afastar: escala a distância mantendo a direção.
  const deslocamento = camera.position.clone().sub(alvoAtual)
  const distancia = deslocamento.length()
  if (!Number.isFinite(distancia) || distancia === 0) return null

  const fator = comando.tipo === 'aproximar' ? 0.78 : 1.28
  const proxima = Math.min(DISTANCIA_MAXIMA, Math.max(DISTANCIA_MINIMA, distancia * fator))
  deslocamento.multiplyScalar(proxima / distancia)
  return { posicao: alvoAtual.clone().add(deslocamento), alvo: alvoAtual.clone() }
}

type CameraEnquadravel = { position: Vector3; fov?: number; aspect?: number }

/** Mesmo teto do `maxDistance` do Table top em `Visualizador3D`. */
const DISTANCIA_MAXIMA_CENA = 30

/**
 * A cena montada inteira no quadro. Diferente de enquadrar uma peça, a cena é
 * espalhada no chão e vista de três quartos: o maior lado da caixa não basta
 * (a diagonal escapava pelas bordas), então é a esfera que envolve a caixa que
 * precisa caber no campo mais estreito. A distância pode passar da de um
 * veículo só; o `maxDistance` dos controles de órbita de cada módulo limita.
 */
function enquadrarCena(caixa: Box3, camera: CameraEnquadravel, alvoAtual: Vector3): Pose {
  const esfera = caixa.getBoundingSphere(new Sphere())
  const fov = camera.fov ?? 42
  const proporcao = Math.min(1, camera.aspect ?? 1)
  const meioCampo = Math.atan(Math.tan((fov * Math.PI) / 360) * proporcao)
  const distancia = Math.min(DISTANCIA_MAXIMA_CENA, Math.max(DISTANCIA_MINIMA, esfera.radius / Math.sin(meioCampo)))
  return manterDirecao(esfera.center, distancia, camera, alvoAtual)
}

/** Mantém a direção de onde já se olhava: enquadrar não deve teletransportar o aluno para o outro lado do veículo. */
function manterDirecao(centro: Vector3, distancia: number, camera: CameraEnquadravel, alvoAtual: Vector3): Pose {
  const direcao = camera.position.clone().sub(alvoAtual)
  if (direcao.lengthSq() === 0) direcao.set(-1, 0.7, 1)
  direcao.normalize().multiplyScalar(distancia)
  return { posicao: centro.clone().add(direcao), alvo: centro.clone() }
}

/** Distância e direção que põem a caixa inteira no quadro, mantendo o lado de onde já se olhava. */
/** Acima desta distância, escolher uma peça pequena aproxima a câmera dela. */
export const DISTANCIA_PARA_APROXIMAR_M = 6

function enquadrarCaixa(caixa: Box3, camera: CameraEnquadravel, alvoAtual: Vector3): Pose {
  const centro = caixa.getCenter(new Vector3())
  const tamanho = caixa.getSize(new Vector3())
  const maiorLado = Math.max(tamanho.x, tamanho.y, tamanho.z)

  // Distância que põe o maior lado dentro do campo de visão, com folga para a
  // peça não encostar nas bordas. Com a tela em pé (celular), o campo
  // horizontal é o mais estreito e é ele que manda — só o vertical cortava a
  // peça dos lados.
  const fov = camera.fov ?? 42
  const proporcao = Math.min(1, camera.aspect ?? 1)
  const tangenteMeioCampo = Math.tan((fov * Math.PI) / 360) * proporcao
  const distancia = Math.min(DISTANCIA_MAXIMA, Math.max(DISTANCIA_MINIMA, (maiorLado * 1.6) / (2 * tangenteMeioCampo)))
  return manterDirecao(centro, distancia, camera, alvoAtual)
}

/**
 * Acha na cena o objeto a enquadrar.
 *
 * Os módulos marcam a seleção de formas diferentes: peças e objetos do table top
 * carregam `selecionavel:<id>`, e a estabilização dá um nome fixo ao grupo do
 * apoio selecionado. Procurar os dois evita ter que uniformizar a marcação das
 * cenas, que é código didático e está fora do escopo do redesenho.
 */
function acharSelecionado(cena: Object3D, id: string | null | undefined): Object3D | null {
  if (id) {
    const porId = cena.getObjectByName(`${PREFIXO_SELECIONAVEL}${id}`)
    if (porId) return porId
  }
  return cena.getObjectByName(NOME_APOIO_SELECIONADO) ?? null
}
